import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2,
  Smartphone,
  Check,
  Copy,
  AlertTriangle,
  ArrowLeft,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useTwoFactorAuthStore } from '@/store/twoFactorAuth';
import { useTwoFactor } from '@/hooks/useTwoFactor';
import { supabase } from '@/lib/supabase';

type SetupStep = 'intro' | 'qr' | 'verify' | 'backup' | 'complete';

export default function TwoFactorSetupRequired() {
  const navigate = useNavigate();
  const { pendingAuth, clearPendingAuth } = useTwoFactorAuthStore();
  const {
    setupData,
    backupCodes,
    initializeSetup,
    verifyAndEnable,
  } = useTwoFactor();

  const [step, setStep] = useState<SetupStep>('intro');
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedBackupCodes, setCopiedBackupCodes] = useState(false);

  // Redirect if no pending auth
  useEffect(() => {
    if (!pendingAuth || !pendingAuth.requires2FASetup) {
      navigate('/login');
    }
  }, [pendingAuth, navigate]);

  const handleStartSetup = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await initializeSetup();
      setStep('qr');
    } catch (err) {
      console.error('Failed to initialize 2FA setup:', err);
      setError('Failed to start setup. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (verificationCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const codes = await verifyAndEnable.mutateAsync(verificationCode);
      if (codes) {
        setStep('backup');
      }
    } catch (err) {
      console.error('Failed to verify code:', err);
      setError('Invalid verification code. Please try again.');
      setVerificationCode('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyBackupCodes = () => {
    if (backupCodes) {
      navigator.clipboard.writeText(backupCodes.join('\n'));
      setCopiedBackupCodes(true);
      setTimeout(() => setCopiedBackupCodes(false), 2000);
    }
  };

  const handleComplete = async () => {
    // Get session token for device tracking
    const sessionToken = localStorage.getItem('blazepos_session_token') || '';

    if (pendingAuth) {
      // Record verification
      await supabase.rpc('record_2fa_verification', {
        p_user_id: pendingAuth.userId,
        p_organization_id: pendingAuth.organizationId,
        p_device_token: sessionToken,
        p_trust_device: true, // Trust this device since they just set up 2FA
      });
    }

    clearPendingAuth();
    navigate('/dashboard');
  };

  const handleCancel = () => {
    clearPendingAuth();
    supabase.auth.signOut();
    navigate('/login');
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '');
    if (digits.length <= 6) {
      setVerificationCode(digits);
      setError(null);
    }
  };

  if (!pendingAuth || !pendingAuth.requires2FASetup) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col bg-zinc-100 dark:bg-zinc-950">
      {/* Header Bar */}
      <header className="bg-zinc-950 dark:bg-black text-white px-6 py-3 flex items-center justify-between border-b border-zinc-900">
        <div className="flex items-center gap-2">
          <img src="/blazepos-logo.png" alt="BlazePOS" className="h-6 w-6 object-contain" />
          <span className="font-semibold text-lg tracking-tight">BlazePOS</span>
        </div>
        {step === 'intro' && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            className="text-zinc-300 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Login
          </Button>
        )}
      </header>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md bg-white dark:bg-zinc-900 rounded-xl p-8 shadow-lg">
          {/* Intro Step */}
          {step === 'intro' && (
            <div className="text-center space-y-6">
              <div className="w-16 h-16 mx-auto rounded-full bg-white dark:bg-zinc-800 flex items-center justify-center shadow-sm border border-zinc-200 dark:border-zinc-700">
                <img src="/blazepos-logo.png" alt="BlazePOS" className="w-10 h-10 object-contain" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-zinc-900 dark:text-white mb-2">
                  Two-Factor Authentication Required
                </h1>
                <p className="text-zinc-500 dark:text-zinc-400 text-sm">
                  Your role requires two-factor authentication for added security. Please set it up to continue.
                </p>
              </div>
              <div className="p-4 bg-amber-50 dark:bg-amber-950/30 rounded-lg">
                <p className="text-sm text-amber-700 dark:text-amber-400 flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  You will need an authenticator app like Google Authenticator, Authy, or 1Password.
                </p>
              </div>
              <Button onClick={handleStartSetup} disabled={isLoading} className="w-full">
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Setting up...
                  </>
                ) : (
                  <>
                    <Smartphone className="h-4 w-4 mr-2" />
                    Set Up Two-Factor Authentication
                  </>
                )}
              </Button>
              {error && <p className="text-sm text-red-500">{error}</p>}
            </div>
          )}

          {/* QR Code Step */}
          {step === 'qr' && setupData && (
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-xl font-bold text-zinc-900 dark:text-white mb-2">
                  Scan QR Code
                </h2>
                <p className="text-zinc-500 dark:text-zinc-400 text-sm">
                  Scan this code with your authenticator app
                </p>
              </div>

              <div className="flex justify-center p-4 bg-white rounded-lg border">
                <img
                  src={setupData.qrCodeDataUrl}
                  alt="2FA QR Code"
                  className="w-48 h-48"
                />
              </div>

              <div className="text-center">
                <p className="text-xs text-muted-foreground mb-2">
                  Or enter this code manually:
                </p>
                <code className="px-3 py-1.5 bg-muted rounded text-sm font-mono break-all">
                  {setupData.secret}
                </code>
              </div>

              <Button onClick={() => setStep('verify')} className="w-full">
                Continue
              </Button>
            </div>
          )}

          {/* Verify Step */}
          {step === 'verify' && (
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-xl font-bold text-zinc-900 dark:text-white mb-2">
                  Verify Setup
                </h2>
                <p className="text-zinc-500 dark:text-zinc-400 text-sm">
                  Enter the 6-digit code from your authenticator app
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="verification-code">Verification Code</Label>
                <Input
                  id="verification-code"
                  type="text"
                  inputMode="numeric"
                  value={verificationCode}
                  onChange={handleCodeChange}
                  placeholder="000000"
                  className={`text-center text-2xl tracking-widest font-mono ${
                    error ? 'border-red-500' : ''
                  }`}
                  disabled={isLoading}
                  autoFocus
                />
                {error && <p className="text-sm text-red-500 text-center">{error}</p>}
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep('qr')} className="flex-1">
                  Back
                </Button>
                <Button
                  onClick={handleVerifyCode}
                  disabled={isLoading || verificationCode.length !== 6}
                  className="flex-1"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify'
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Backup Codes Step */}
          {step === 'backup' && backupCodes && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-12 h-12 mx-auto rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
                  <Check className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <h2 className="text-xl font-bold text-zinc-900 dark:text-white mb-2">
                  Save Your Backup Codes
                </h2>
                <p className="text-zinc-500 dark:text-zinc-400 text-sm">
                  Save these codes in a safe place. You can use them if you lose access to your authenticator.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2 p-4 bg-muted rounded-lg">
                {backupCodes.map((code, index) => (
                  <code key={index} className="text-sm font-mono text-center py-1">
                    {code}
                  </code>
                ))}
              </div>

              <Button
                variant="outline"
                className="w-full gap-2"
                onClick={handleCopyBackupCodes}
              >
                {copiedBackupCodes ? (
                  <>
                    <Check className="h-4 w-4" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    Copy All Codes
                  </>
                )}
              </Button>

              <div className="p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg">
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  <AlertTriangle className="h-3.5 w-3.5 inline mr-1" />
                  Each backup code can only be used once. Store them securely.
                </p>
              </div>

              <Button onClick={handleComplete} className="w-full">
                Continue to Dashboard
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
